import React from 'react';
import PropTypes from 'prop-types';
import { DayPicker, TZDate } from 'react-day-picker';
import { useTimezoneSelect, allTimezones } from 'react-timezone-select';
import {
  Box, Text,
} from 'grommet';
import { AppButton } from '@Components/Control';
import { useAppTheme } from '@Components/Context';
import { ProductFormDropdownSelect } from '@Components/Partial/Product/NewSearch';
import { dateToISOString } from '@Helpers/util';
import {
  StyledCalendarContainer, StyledTimeInput, StyledCalendarSelect,
} from './StyledCalendar';
/* eslint-disable-next-line import/no-unresolved */
import 'react-day-picker/style.css';

const DatetimeCalendar = ({
  small,
  calendarMode,
  setDate = null,
  setDateRange = null,
  timeZone = null,
  setTimeZone = null,
  hideTimeZone = false,
  dateValue = null,
  dateRangeValue = null,
  showTimeSelect = false,
  minStartDate = null,
  maxEndDate = null,
  handleClose = null,
  showCloseButton = false,
  ...rest
}) => {
  const { theme: { page, button } } = useAppTheme();

  const initialSelected = React.useMemo(() => {
    if (calendarMode === 'range') {
      if (dateRangeValue && dateRangeValue.from && dateRangeValue.to) {
        return {
          from: dateRangeValue.from
            ? new TZDate(new Date(dateRangeValue.from), timeZone)
            : undefined,
          to: dateRangeValue.to
            ? new TZDate(new Date(dateRangeValue.to), timeZone)
            : undefined,
        };
      }
      return { from: undefined, to: undefined };
    }

    return dateValue
      ? new TZDate(new Date(dateValue), timeZone)
      : new TZDate(new Date(), timeZone);
  }, [calendarMode, dateValue, dateRangeValue, timeZone]);
  const [selected, setSelected] = React.useState(initialSelected);

  let initialTime = { hour: 0, minute: 0 };
  if (calendarMode === 'single' && dateValue && initialSelected instanceof Date) {
    initialTime = {
      hour: initialSelected.getHours(),
      minute: initialSelected.getMinutes(),
    };
  }
  const [selectedTime, setSelectedTime] = React.useState(initialTime);
  const { options, parseTimezone } = useTimezoneSelect({ labelStyle: 'abbrev', allTimezones });

  const handleTimeZoneSelect = (timeVal) => {
    const parsedTimezone = parseTimezone(timeVal);
    setTimeZone(parsedTimezone.value);

    // When timezone changes, update selected date to new TZDate in that zone
    const newTZDate = new TZDate(selected, parsedTimezone.value);
    setSelected(newTZDate);
    setSelectedTime({
      hour: newTZDate.getHours(),
      minute: newTZDate.getMinutes(),
    });
    // Update parent with new ISO string
    const isoString = dateToISOString(newTZDate, parsedTimezone.value);
    setDate(isoString);
  };

  const handleDateSelect = (dateVal) => {
    if (!dateVal) return;

    if (calendarMode === 'range') {
      setSelected(dateVal);
      if (dateVal.from && dateVal.to && setDateRange) {
        const toLocalDateStr = (d) => {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${y}-${m}-${day}`;
        };
        const fromStr = dateVal.from instanceof Date ? toLocalDateStr(dateVal.from) : '';
        const toStr = dateVal.to instanceof Date ? toLocalDateStr(dateVal.to) : '';
        setDateRange(fromStr, toStr);
      }
      return;
    }

    const newDate = new TZDate(dateVal, timeZone);
    if (showTimeSelect) {
      newDate.setHours(selectedTime.hour, selectedTime.minute, 0, 0);
    }
    setSelected(newDate);
    const isoString = dateToISOString(newDate, timeZone);
    setDate(isoString);
  };

  const getTimeValue = () => {
    const h = String(selectedTime.hour).padStart(2, '0');
    const m = String(selectedTime.minute).padStart(2, '0');
    return `${h}:${m}`;
  };

  const handleTimeInputChange = (e) => {
    const [hour, minute] = e.target.value.split(':').map(Number);
    setSelectedTime({ hour, minute });
    const newDate = new TZDate(selected, timeZone);
    newDate.setHours(hour, minute, 0, 0);
    setSelected(newDate);
    const isoString = dateToISOString(newDate, timeZone);
    setDate(isoString);
  };

  const formattedTimezones = options.map(({ value, label }) => ({
    api: value,
    display: label,
  }));

  const disabledDates = minStartDate || maxEndDate ? {
    before: minStartDate ? new Date(minStartDate) : undefined,
    after: maxEndDate ? new Date(maxEndDate) : undefined,
  } : undefined;

  const now = new Date();
  let defaultMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  let startMonth = new Date(now.getFullYear() - 4, now.getMonth(), 1);
  let endMonth = new Date(now.getFullYear() + 1, now.getMonth(), 1);

  if (minStartDate instanceof Date && !Number.isNaN(minStartDate.getTime())) {
    startMonth = new Date(minStartDate.getFullYear(), minStartDate.getMonth(), 1);
    if (minStartDate > now) defaultMonth = startMonth;
  }

  if (maxEndDate instanceof Date && !Number.isNaN(maxEndDate.getTime())) {
    endMonth = new Date(maxEndDate.getFullYear(), maxEndDate.getMonth(), 1);
    if (maxEndDate < now) defaultMonth = endMonth;
  }

  /* eslint-disable react/prop-types */
  const CustomSelectDropdown = (props) => {
    const { options: dateOptions, value: dateVal, onChange } = props;

    const handleChange = (e) => {
      if (onChange) {
        onChange(e);
      }
    };

    return (
      <StyledCalendarSelect
        value={dateVal}
        onChange={handleChange}
        $inputBorder={page.inputBorder}
        $primaryText={page.inputText}
        $bgColor={page.inputBg}
      >
        {dateOptions?.map((opt) => (
          <option
            key={opt.value}
            value={opt.value.toString()}
            disabled={opt.disabled}
          >
            {opt.label}
          </option>
        ))}
      </StyledCalendarSelect>
    );
  };
  /* eslint-enable react/prop-types */

  return (
    <StyledCalendarContainer
      align="center"
      pad="1rem"
      gap="1rem"
      $bgColor={page.modalBg}
      $primaryText={page.modalText}
      $buttonHighlight={button.primaryBg}
      $todayHighlight={page.highlight}
      $borderHighlight={page.focusHighlight}
      {...rest}
    >
      {!hideTimeZone && timeZone && setTimeZone && (
        <Box
          direction="row"
          align="start"
          justify="center"
          gap="1rem"
          width="100%"
        >
          <Box direction="row" gap="0.5rem" width="100%" justify="between" align="center">
            <Text size="1rem" style={{ whiteSpace: 'nowrap' }} color={page.modalText}>
              Time zone:&nbsp;
            </Text>
            <ProductFormDropdownSelect
              selectable
              small={small}
              inputConfig={{ overrideOptions: formattedTimezones }}
              handleFormValues={(value) => handleTimeZoneSelect(value)}
              label={timeZone}
              value={timeZone}
              autoSelect={false}
            />
          </Box>
        </Box>
      )}
      {showTimeSelect && (
        <Box
          direction="row"
          align="start"
          justify="center"
          gap="1rem"
          width="100%"
        >
          <Box direction="row" gap="0.5rem" width="100%" justify="between" align="center">
            <Text size="1rem" color={page.modalText}>Time:</Text>
            <StyledTimeInput
              type="time"
              value={getTimeValue()}
              onChange={handleTimeInputChange}
              $focusHighlight={page.focusHighlight}
              $primaryText={page.inputText}
              $bgColor={page.inputBg}
              $inputBorder={page.inputBorder}
              step={600}
            />
          </Box>
        </Box>
      )}
      <DayPicker
        fixedWeeks
        reverseYears
        mode={calendarMode}
        selected={selected}
        onSelect={handleDateSelect}
        disabled={disabledDates}
        timeZone={timeZone}
        navLayout="after"
        captionLayout="dropdown"
        components={{
          Dropdown: CustomSelectDropdown,
        }}
        defaultMonth={defaultMonth}
        startMonth={startMonth}
        endMonth={endMonth}
      />
      {showCloseButton && handleClose && (
        <Box>
          <AppButton
            overrideHover
            onClick={() => handleClose()}
            label="Finished"
            level="dynamicSmall"
            color={button.primaryText}
            bgColor={button.primaryBg}
          />
        </Box>
      )}
    </StyledCalendarContainer>
  );
};

DatetimeCalendar.propTypes = {
  small: PropTypes.bool.isRequired,
  calendarMode: PropTypes.oneOf(['single', 'range']).isRequired,
  setDate: PropTypes.func,
  setDateRange: PropTypes.func,
  dateValue: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  dateRangeValue: PropTypes.shape({
    from: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    to: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  }),
  showTimeSelect: PropTypes.bool,
  timeZone: PropTypes.string,
  setTimeZone: PropTypes.func,
  hideTimeZone: PropTypes.bool,
  minStartDate: PropTypes.instanceOf(Date),
  maxEndDate: PropTypes.instanceOf(Date),
  handleClose: PropTypes.func,
  showCloseButton: PropTypes.bool,
};

export default DatetimeCalendar;
